import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Alert,
  ArrowsNavigation,
  BackListButton,
  DocumentsCard,
  shipmentStatuses,
  ToastContext,
} from '@reveni-io/reveni-ui'

import EditAddressModal from '@/common/components/EditAddressModal/EditAddressModal'
import EditCustomsInfoModal from '@/common/components/EditCustomsInfoModal/EditCustomsInfoModal'
import OpenClaimModal from '@/common/components/OpenClaimModal/OpenClaimModal'
import SendEmailDrawer from '@/common/components/SendEmailDrawer/SendEmailDrawer'
import { emailTypes } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import { openInNewTab } from '@/common/helpers/helpers'
import useCustomsInformation from '@/common/hooks/useCustomsInformation/useCustomsInformation'
import useLocations from '@/common/hooks/useLocations/useLocations'
import useOpenClaim from '@/common/hooks/useOpenClaim/useOpenClaim'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import ParcelInfoHeader from '@/parcels/components/ParcelInfoHeader/ParcelInfoHeader'
import { parcelDirections } from '@/parcels/constants'
import {
  getParcelCustomerAddress,
  getParcelSenderName,
  getParcelSequenceInfo,
  isInboundParcel,
  isInternationalAddresses,
  isTrackingParcel,
} from '@/parcels/helpers/helpers'
import useParcel from '@/parcels/hooks/useParcel/useParcel'
import usePrepareParcelItems from '@/parcels/hooks/usePrepareParcelItems/usePrepareParcelItems'
import parcelServices from '@/parcels/services/services'
import routes from '@/Router/routes.constants'

import CancelParcelModal from './CancelParcelModal/CancelParcelModal'
import ClaimsCard from './ClaimsCard/ClaimsCard'
import CustomerDetailCard from './CustomerDetailCard/CustomerDetailCard'
import EditCarrierServiceModal from './EditCarrierServiceModal/EditCarrierServiceModal'
import EditLocationModal from './EditLocationModal/EditLocationModal'
import useAiEmailGeneration from './hooks/useAiEmailGeneration/useAiEmailGeneration'
import useCancelParcel from './hooks/useCancelParcel/useCancelParcel'
import useCarrierService from './hooks/useCarrierService/useCarrierService'
import useGenerateLabel from './hooks/useGenerateLabel/useGenerateLabel'
import useMarkParcelAlertAsRead from './hooks/useMarkParcelAlertAsRead/useMarkParcelAlertAsRead'
import useParcelInboundDestinationLocation from './hooks/useParcelInboundDestinationLocation/useParcelInboundDestinationLocation'
import useParcelInboundOriginAddress from './hooks/useParcelInboundOriginAddress/useParcelInboundOriginAddress'
import useParcelNavigation from './hooks/useParcelNavigation/useParcelNavigation'
import useParcelOutboundDestinationAddress from './hooks/useParcelOutboundDestinationAddress/useParcelOutboundDestinationAddress'
import useParcelOutboundOriginLocation from './hooks/useParcelOutboundOriginLocation/useParcelOutboundOriginLocation'
import useResetParcel from './hooks/useResetParcel/useResetParcel'
import useSendEmail from './hooks/useSendEmail/useSendEmail'
import ParcelAlert from './ParcelAlert/ParcelAlert'
import ParcelDetailPageSkeleton from './ParcelDetailPageSkeleton/ParcelDetailPageSkeleton'
import ParcelInfoCard from './ParcelInfoCard/ParcelInfoCard'
import ParcelStatus from './ParcelStatus/ParcelStatus'
import ParcelTimelineCard from './ParcelTimelineCard/ParcelTimelineCard'
import ProductsCard from './ProductsCard/ProductsCard'
import { getRelatedInformationItems } from './RelatedInformationCard/helpers'
import RelatedInformationCard from './RelatedInformationCard/RelatedInformationCard'
import ResetParcelModal from './ResetParcelModal/ResetParcelModal'
import ShippingCostCard from './ShippingCostCard/ShippingCostCard'
import { canSplitParcel, getLabel } from './helpers'
import * as Styles from './styles'

const getItemsCustomsInfoService = ({ resourceId, storeId }) => {
  return parcelServices.getItemsCustomsInfo({ relatedId: resourceId, storeId })
}

const editItemsCustomsInfoService = ({ resourceId, storeId, payload }) => {
  return parcelServices.editItemsCustomsInfo({ relatedId: resourceId, storeId, payload })
}

const ParcelDetailPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)

  const navigation = useNavigate()
  const { parcel, loading, getParcel } = useParcel({ id, storeId: info?.selectedStore?.id })
  const { locations } = useLocations({ storeId: info?.selectedStore?.id })
  const { onNavigateToParcelDetail, onGetMoreParcels } = useParcelNavigation({
    storeId: info?.selectedStore?.id,
    parcelId: id,
    parcel,
    fetchParcel: getParcel,
    info,
    updateContext,
  })

  const { onPrepareParcel, onAutoPrepareParcel, preparingParcel, allowPrepareParcel, allowAmendPackingStatus } =
    usePrepareParcelItems({
      items: parcel?.related?.line_items,
      storeId: info?.selectedStore?.id,
      parcelId: id,
      relatedId: parcel?.related?.id,
      navigation,
      setToastInfo,
      fetchParcel: getParcel,
      updateContext,
      parcel,
    })

  const onParcelEdited = updatedParcel => {
    navigation(routes.parcelDetailPage.replace(':id', updatedParcel.id))
  }

  const {
    showEditOutboundDestinationAddressModal,
    openEditOutboundDestinationAddressModal,
    closeEditOutboundDestinationAddressModal,
    onEditOutboundDestinationAddress,
    editingAddress,
    allowEditOutboundDestinationAddress,
  } = useParcelOutboundDestinationAddress({
    storeId: info?.selectedStore?.id,
    parcelId: id,
    refetchParcel: getParcel,
    setToastInfo,
    requiresCustomerTaxId: parcel?.related?.requires_customer_tax_id,
    parcel,
    isPickupPoint: parcel?.related?.shipping_method?.is_pickup_point,
  })

  const {
    showEditOutboundOriginLocationModal,
    openEditOutboundOriginLocationModal,
    closeEditOutboundOriginLocationModal,
    onEditOutboundOriginLocation,
    editingOutboundOriginLocation,
    allowEditOutboundOriginLocation,
  } = useParcelOutboundOriginLocation({
    storeId: info?.selectedStore?.id,
    parcelId: id,
    refetchParcel: getParcel,
    setToastInfo,
    parcel,
  })

  const {
    showEditInboundOriginAddressModal,
    openEditInboundOriginAddressModal,
    closeEditInboundOriginAddressModal,
    onEditInboundOriginAddress,
    loading: editingInboundOriginAddress,
    allowEditInboundOriginAddress,
  } = useParcelInboundOriginAddress({
    storeId: info?.selectedStore?.id,
    parcelId: id,
    onParcelEdited,
    setToastInfo,
    parcel,
  })

  const {
    showEditInboundDestinationLocationModal,
    openEditInboundDestinationLocationModal,
    closeEditInboundDestinationLocationModal,
    onEditInboundDestinationLocation,
    loading: editingInboundDestinationLocation,
    allowEditInboundDestinationLocation,
  } = useParcelInboundDestinationLocation({
    storeId: info?.selectedStore?.id,
    parcelId: id,
    onParcelEdited,
    setToastInfo,
    parcel,
  })

  const {
    showEditCarrierServiceModal,
    carrierServices,
    onEditCarrierService,
    openEditCarrierServiceModal,
    closeEditCarrierServiceModal,
    loadingCarrierServices,
    editingCarrierService,
    allowEditCarrierService,
  } = useCarrierService({
    storeId: info?.selectedStore?.id,
    parcelId: id,
    refetchParcel: getParcel,
    setToastInfo,
    parcel,
  })

  const {
    showCancelParcelModal,
    openCancelParcelModal,
    closeCancelParcelModal,
    onCancelParcel,
    cancelingParcel,
    allowCancelParcel,
    isCancellable,
    loadingCancellableDetail,
  } = useCancelParcel({
    storeId: info?.selectedStore?.id,
    relatedId: parcel?.related?.id,
    refetchParcel: getParcel,
    parcel,
    setToastInfo,
  })

  const {
    showResetParcelModal,
    openResetParcelModal,
    closeResetParcelModal,
    onResetParcel,
    resettingParcel,
    allowResetParcel,
  } = useResetParcel({
    storeId: info?.selectedStore?.id,
    relatedId: parcel?.related?.id,
    refetchParcel: getParcel,
    parcel,
    setToastInfo,
  })

  const { onGenerateLabel, generatingLabel } = useGenerateLabel({
    storeId: info?.selectedStore?.id,
    parcel,
    refetchParcel: getParcel,
  })

  const { markParcelAlertAsRead } = useMarkParcelAlertAsRead({
    storeId: info?.selectedStore?.id,
    parcelId: id,
    refetchParcel: getParcel,
  })

  const {
    to,
    setTo,
    cc,
    setCc,
    replyTo,
    setReplyTo,
    subject,
    setSubject,
    body,
    setBody,
    showSendEmailModal,
    openSendEmailModalForContactCustomer,
    openSendEmailModalForContactCarrier,
    closeSendEmailModal,
    destination,
    copies: sendEmailModalCopies,
    onSendEmail,
    loading: sendingEmail,
    allowSendEmail,
  } = useSendEmail({
    storeId: info?.selectedStore?.id,
    relatedId: parcel?.related?.id,
    setToastInfo,
    refetchParcel: getParcel,
    customerEmail: parcel?.order?.email,
    storeReplyToEmail: info?.selectedStore?.reply_to_email,
    carrierEmail: parcel?.method?.carrier_email,
    parcel,
  })

  const {
    generatedSubject,
    generatedBody,
    generating: generatingAiEmail,
    aiFlowState,
    userInquiry,
    setUserInquiry,
    onGenerateAiEmail,
    acceptAiContent,
    requestImprovement,
    discardAiContent,
    cancelGeneration,
  } = useAiEmailGeneration({
    storeId: info?.selectedStore?.id,
    relatedId: parcel?.related?.id,
    setToastInfo,
    setSubject,
    setBody,
    destination,
    subject,
    body,
    showSendEmailModal,
  })

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })

  const {
    showItemsCustomsInfoModal,
    loadingItemsCustomsInfo,
    lineItemsCustomsInfo,
    loadingEditItemsCustomsInfo,
    onChangeItemCustomsInfo,
    onConfirmItemsCustomsInfo,
    openEditCustomsInformationModal,
    regions,
    loadingRegions,
    onCloseCustomsInformationModal,
  } = useCustomsInformation({
    storeId: info?.selectedStore?.id,
    resourceId: parcel?.related?.id,
    lineItems: parcel?.related?.line_items || [],
    setToastInfo,
    editCustomsInformationService: editItemsCustomsInfoService,
    onGenerateLabel,
    getCustomsInformationService: getItemsCustomsInfoService,
  })

  const allowSplitParcel = canSplitParcel(parcel)
  const handleSplitParcel = () => navigation(routes.parcelSplitPage.replace(':id', id))

  const handleClickCarrierTrackingPage = () => openInNewTab(parcel?.tracking_url)
  const handleClickViewInStore = () => openInNewTab(parcel?.order?.external_url)
  const handleClickOrderTrackingPage = () => openInNewTab(parcel?.order?.order_tracking_page_url)

  const parcelLabels = isInboundParcel(parcel) ? parcel?.related?.shipping.return_labels : parcel?.related?.labels
  const claimLabel = getLabel(parcelLabels, parcel?.tracking_number)
  const allowOpenClaim = claimLabel?.carrier_service?.is_reveni_contract

  const { showOpenClaimModal, openOpenClaimModal, closeOpenClaimModal, onOpenClaim, openingClaim, uploadClaimImage } =
    useOpenClaim({
      storeId: info?.selectedStore?.id,
      fetchData: getParcel,
      fetchTimeline: getParcel,
      setToastInfo,
      openClaimService: parcelServices.openClaim,
      uploadImageService: parcelServices.uploadParcelImage,
      resourceId: id,
    })

  const direction = isInboundParcel(parcel) ? parcelDirections.inbound : parcelDirections.outbound
  const isTracking = isTrackingParcel(parcel)
  const senderAddress = parcel?.origin
  const destinationAddress = parcel?.destination
  const isInternational = isInternationalAddresses(senderAddress, destinationAddress)
  const sequenceInfo = getParcelSequenceInfo(parcel)

  if (loading || !parcel) {
    return <ParcelDetailPageSkeleton />
  }

  return (
    <Styles.Container>
      <Styles.NavigationContainer>
        <BackListButton to={routes.parcelsPage.index}>{t('Volver al listado')}</BackListButton>
        <ArrowsNavigation
          currentElementId={id}
          elements={info?.parcels?.results}
          hasNext={!!info?.parcels?.next}
          hasPrevious={!!info?.parcels?.previous}
          onNavigate={onNavigateToParcelDetail}
          refreshElements={onGetMoreParcels}
        />
      </Styles.NavigationContainer>
      <ParcelAlert
        alert={parcel?.alert}
        parcelStatus={parcel?.status}
        createdAt={parcel?.created_at}
        packedAt={parcel?.packed_at}
        readyAt={parcel?.ready_at}
        shippedAt={parcel?.shipped_at}
        outForDeliveryAt={parcel?.out_for_delivery_at}
        deliveredAt={parcel?.delivered_at}
        normalizedTrackingStatus={parcel?.normalized_tracking_status}
        onGenerateLabel={onGenerateLabel}
        generatingLabel={generatingLabel}
        openEditOutboundDestinationAddressModal={openEditOutboundDestinationAddressModal}
        openSendEmailModalForContactCustomer={openSendEmailModalForContactCustomer}
        openSendEmailModalForContactCarrier={openSendEmailModalForContactCarrier}
        markParcelAlertAsRead={markParcelAlertAsRead}
        openEditCustomsModal={openEditCustomsInformationModal}
      />
      <ParcelInfoHeader
        orderName={parcel?.order?.name}
        parcelType={parcel?.type}
        isExchange={parcel?.order?.is_exchange}
        isInternational={isInternational}
        withCustoms={parcel?.related?.with_customs}
        senderAddress={senderAddress}
        recipientAddress={destinationAddress}
        status={parcel?.status}
        sequenceNumber={sequenceInfo?.position}
        parcelsInOrder={sequenceInfo?.total}
        isInbound={isInboundParcel(parcel)}
        onGenerateLabel={onGenerateLabel}
        generatingLabel={generatingLabel}
        openEditOutboundDestinationAddressModal={openEditOutboundDestinationAddressModal}
        allowEditOutboundDestinationAddress={allowEditOutboundDestinationAddress}
        openEditOutboundOriginLocationModal={openEditOutboundOriginLocationModal}
        allowEditOutboundOriginLocation={allowEditOutboundOriginLocation}
        openEditInboundOriginAddressModal={openEditInboundOriginAddressModal}
        allowEditInboundOriginAddress={allowEditInboundOriginAddress}
        openEditInboundDestinationLocationModal={openEditInboundDestinationLocationModal}
        allowEditInboundDestinationLocation={allowEditInboundDestinationLocation}
        openEditCarrierServiceModal={openEditCarrierServiceModal}
        allowEditCarrierService={allowEditCarrierService}
        openResetParcelModal={openResetParcelModal}
        allowResetParcel={allowResetParcel}
        openCancelParcelModal={openCancelParcelModal}
        allowCancelParcel={allowCancelParcel}
        onPrepareParcel={onPrepareParcel}
        onAutoPrepareParcel={onAutoPrepareParcel}
        preparingParcel={preparingParcel}
        allowPrepareParcel={allowPrepareParcel}
        allowAmendPackingStatus={allowAmendPackingStatus}
        shippingSkipPacking={info?.selectedStore?.preferences?.shipping_skip_packing}
        onSplitParcel={handleSplitParcel}
        allowSplitParcel={allowSplitParcel}
        openSendEmailModalForContactCustomer={openSendEmailModalForContactCustomer}
        openSendEmailModalForContactCarrier={openSendEmailModalForContactCarrier}
        allowSendEmail={allowSendEmail}
        onClickCarrierTrackingPage={handleClickCarrierTrackingPage}
        displayCarrierTrackingPageAction={returnPortalFeatures?.display_carrier_tracking_page_action}
        onClickViewInStore={handleClickViewInStore}
        viewInStoreUrl={parcel?.order?.external_url}
        onClickOrderTrackingPage={handleClickOrderTrackingPage}
        orderTrackingPageUrl={parcel?.order?.order_tracking_page_url}
        onOpenClaim={openOpenClaimModal}
        allowOpenClaim={allowOpenClaim}
      />
      <Styles.MainContainer>
        <Styles.ColumnContainer>
          <ParcelStatus
            direction={direction}
            parcelStatus={parcel?.status}
            createdAt={parcel?.created_at}
            packedAt={parcel?.packed_at}
            readyAt={parcel?.ready_at}
            shippedAt={parcel?.shipped_at}
            outForDeliveryAt={parcel?.out_for_delivery_at}
            deliveredAt={parcel?.delivered_at}
            timeZone={info?.selectedStore?.timezone}
            normalizedTrackingStatus={parcel?.normalized_tracking_status}
            trackingNumber={parcel?.tracking_number}
            trackingUrl={parcel?.order?.order_tracking_page_url}
            customerReviewRating={parcel?.rating}
            pickupDate={parcel?.pickup_scheduled}
            eta={parcel?.related?.eta}
          />
          <ParcelInfoCard
            parcelType={parcel?.type}
            senderAddress={senderAddress}
            senderName={getParcelSenderName(parcel)}
            recipientAddress={parcel?.related?.recipient_address}
            destinationAddress={destinationAddress}
            shippingMethod={parcel?.method}
            carrierName={parcel?.carrier?.name}
            carrierLogo={parcel?.carrier?.logo_url}
            isPickupPoint={parcel?.related?.shipping_method?.is_pickup_point}
            customerEmail={parcel?.order?.email}
            onSendEmailToCustomer={openSendEmailModalForContactCustomer}
            onSendEmailToCarrier={openSendEmailModalForContactCarrier}
            allowSendEmail={allowSendEmail}
            onEditOutboundOriginLocation={openEditOutboundOriginLocationModal}
            allowEditOutboundOriginLocation={allowEditOutboundOriginLocation}
            onEditOutboundDestinationAddress={openEditOutboundDestinationAddressModal}
            allowEditOutboundDestinationAddress={allowEditOutboundDestinationAddress}
            onEditInboundOriginAddress={openEditInboundOriginAddressModal}
            allowEditInboundOriginAddress={allowEditInboundOriginAddress}
            onEditInboundDestinationLocation={openEditInboundDestinationLocationModal}
            allowEditInboundDestinationLocation={allowEditInboundDestinationLocation}
            onEditCarrierService={openEditCarrierServiceModal}
            allowEditCarrierService={allowEditCarrierService}
          />
          <ProductsCard
            lineItems={parcel?.items || []}
            notes={parcel?.related?.notes}
            isInbound={isInboundParcel(parcel)}
            onPrepareParcel={onPrepareParcel}
            allowPrepareParcel={allowPrepareParcel}
            allowAmendPackingStatus={allowAmendPackingStatus}
            onSplitParcel={handleSplitParcel}
            allowSplitParcel={allowSplitParcel}
          />
          <ParcelTimelineCard parcel={parcel} fetchParcel={getParcel} />
        </Styles.ColumnContainer>
        <Styles.ColumnContainer>
          <RelatedInformationCard
            items={getRelatedInformationItems({ parcel, t })}
            timeZone={info?.selectedStore?.timezone}
          />
          <ClaimsCard claims={parcel?.claims} onOpenClaim={openOpenClaimModal} allowOpenClaim={allowOpenClaim} />
          <CustomerDetailCard
            customerProfile={parcel?.customer_profile}
            email={parcel?.order?.email}
            address={getParcelCustomerAddress(parcel)}
            customerTaxCodes={parcel.order.customer_tax_numbers}
          />
          {!isTracking && (
            <ShippingCostCard
              isInbound={isInboundParcel(parcel)}
              currency={parcel?.related?.currency}
              total={parcel?.related?.total}
              base={parcel?.related?.base}
              duties={parcel?.related?.duties}
              tax={parcel?.related?.tax}
              currencyAdjustment={parcel?.related?.currency_adjustment}
              handlingFees={parcel?.related?.handling_fees}
              additionalProducts={parcel?.related?.additional_products}
            />
          )}
          {!isTracking && (
            <DocumentsCard
              showBorder={false}
              transactionDetail={{
                return_labels: parcel.documents.labels,
                invoice_url: parcel.documents.invoice_url,
              }}
              showCustomsDeclaration
              timeZone={info?.selectedStore?.timezone}
            />
          )}
        </Styles.ColumnContainer>
      </Styles.MainContainer>
      <EditAddressModal
        show={showEditOutboundDestinationAddressModal}
        title={t('Dirección de destino')}
        headerMessage={t('Edita los datos de la dirección del cliente a la que se enviarán los productos')}
        onClose={closeEditOutboundDestinationAddressModal}
        onEditAddress={onEditOutboundDestinationAddress}
        loading={editingAddress}
        shippingAddress={{
          ...parcel?.related?.recipient_address,
          phone: {
            prefix: parcel?.related?.recipient_address?.country?.phone_code,
            number: parcel?.related?.recipient_address?.phone?.number || '',
          },
          customer: {
            tax_id: parcel?.customer_profile?.tax_id,
          },
        }}
        deliveryMethod={{
          requires_address: true,
          type: 'pickuppoint',
        }}
        requiresCustomerTaxId={parcel?.related?.requires_customer_tax_id}
        warningComponent={
          <Alert type="warning">
            {t(
              'Al editar la dirección de destino se creará una nueva etiqueta de envío (y la documentación asociada) que sustituirá a la generada anteriormente.'
            )}
          </Alert>
        }
        showWarningComponent={parcel?.status === shipmentStatuses.ready}
      />
      <EditLocationModal
        show={showEditOutboundOriginLocationModal}
        title={t('Editar origen del envío')}
        headerMessage={t('Elige la ubicación de origen del envío')}
        onClose={closeEditOutboundOriginLocationModal}
        locations={locations}
        currentLocation={parcel?.related?.sender_location}
        onSave={onEditOutboundOriginLocation}
        saving={editingOutboundOriginLocation}
      />
      <EditAddressModal
        show={showEditInboundOriginAddressModal}
        title={t('Dirección de origen')}
        headerMessage={t('Edita los datos de la dirección desde la que se enviarán los productos')}
        onClose={closeEditInboundOriginAddressModal}
        onEditAddress={onEditInboundOriginAddress}
        loading={editingInboundOriginAddress}
        shippingAddress={{
          ...parcel?.origin,
          phone: {
            prefix: parcel?.origin?.country?.phone_code,
            number: parcel?.origin?.phone?.number || '',
          },
        }}
        deliveryMethod={{
          requires_address: true,
          type: 'pickuppoint',
        }}
        warningComponent={
          <Alert type="warning">
            {t(
              'Editar la dirección de origen generará una nueva etiqueta de envío (y la documentación asociada), cancelará este envío de devolución y creará uno nuevo para el mismo pedido.'
            )}
          </Alert>
        }
        showWarningComponent
      />
      <EditLocationModal
        show={showEditInboundDestinationLocationModal}
        title={t('Editar destino del envío')}
        headerMessage={t('Elige la ubicación de destino del envío')}
        onClose={closeEditInboundDestinationLocationModal}
        locations={locations}
        currentLocation={null}
        onSave={onEditInboundDestinationLocation}
        saving={editingInboundDestinationLocation}
        warningComponent={
          <Alert type="warning">
            {t(
              'Editar el destino del envío generará una nueva etiqueta de devolución (y la documentación asociada), cancelará este envío de devolución y creará uno nuevo para el mismo pedido.'
            )}
          </Alert>
        }
      />
      <EditCarrierServiceModal
        show={showEditCarrierServiceModal}
        carrierServices={carrierServices}
        carrierServiceSelected={parcel?.related?.shipping_method?.carrier?.id}
        onClose={closeEditCarrierServiceModal}
        onEditCarrierServices={onEditCarrierService}
        editingCarrierService={editingCarrierService}
        loadingCarrierServices={loadingCarrierServices}
        parcelStatus={parcel?.status}
      />
      <CancelParcelModal
        show={showCancelParcelModal}
        onClose={closeCancelParcelModal}
        onCancelParcel={onCancelParcel}
        cancelingParcel={cancelingParcel}
        isOrderCancellable={isCancellable}
        loading={loadingCancellableDetail}
      />
      <ResetParcelModal
        show={showResetParcelModal}
        onClose={closeResetParcelModal}
        onResetParcel={onResetParcel}
        resettingParcel={resettingParcel}
      />
      <OpenClaimModal
        show={showOpenClaimModal}
        onClose={closeOpenClaimModal}
        onOpenClaim={onOpenClaim}
        loading={openingClaim}
        uploadImage={uploadClaimImage}
      />
      <EditCustomsInfoModal
        show={showItemsCustomsInfoModal}
        items={lineItemsCustomsInfo}
        regions={regions || []}
        loading={loadingItemsCustomsInfo || loadingRegions}
        onChange={onChangeItemCustomsInfo}
        onConfirm={onConfirmItemsCustomsInfo}
        generatingLabel={generatingLabel}
        savingChanges={loadingEditItemsCustomsInfo}
        onClose={onCloseCustomsInformationModal}
      />
      <SendEmailDrawer
        emailType={emailTypes.shipments}
        show={showSendEmailModal}
        onClose={closeSendEmailModal}
        copies={sendEmailModalCopies}
        to={to}
        setTo={setTo}
        cc={cc}
        setCc={setCc}
        replyTo={replyTo}
        setReplyTo={setReplyTo}
        subject={subject}
        setSubject={setSubject}
        body={body}
        setBody={setBody}
        onSendEmail={onSendEmail}
        loading={sendingEmail}
        onGenerateAiEmail={onGenerateAiEmail}
        generatingAiEmail={generatingAiEmail}
        aiEmailGenerated={!!generatedSubject || !!generatedBody}
        aiFlowState={aiFlowState}
        userInquiry={userInquiry}
        setUserInquiry={setUserInquiry}
        onAcceptAiContent={acceptAiContent}
        onRequestImprovement={requestImprovement}
        onDiscardAiContent={discardAiContent}
        onCancelGeneration={cancelGeneration}
        aiEmailGenerationEnabled={returnPortalFeatures?.shipments_ai_generate_email_enabled}
        merchantId={info?.id}
      />
    </Styles.Container>
  )
}

export default ParcelDetailPage
